<template>
    <div class="currency-toggle-wrap" :class="{ 'is-mobile': variant === 'mobile' }" ref="wrap">
        <button
            type="button"
            class="currencySelect"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            aria-label="Select currency"
            @click="toggle"
        >
            <span class="currency-flag-wrap">
                <img v-if="selectedCountry && selectedCountry.flag" :src="selectedCountry.flag" :alt="selectedCountry.name" class="currency-flag" />
            </span>
            <span class="currency-code">{{ selectedCountry ? selectedCountry.currency_code : 'Currency' }}</span>
            <svg class="currency-chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>
        <transition name="currency-menu">
            <div v-if="isOpen" class="currencyMenu" role="listbox" aria-label="Select currency">
                <div class="dropdown-header">Select currency</div>
                <button
                    type="button"
                    v-for="country in countries"
                    :key="country.id"
                    class="dropdown-item"
                    :class="{ active: isSelected(country) }"
                    role="option"
                    :aria-selected="isSelected(country) ? 'true' : 'false'"
                    @click="select(country)"
                >
                    <span class="currency-flag-wrap">
                        <img v-if="country.flag" :src="country.flag" :alt="country.name" class="currency-flag" />
                    </span>
                    <span class="currency-item-text">
                        <span class="currency-item-code">{{ country.currency_code }}</span>
                        <span class="currency-item-symbol">({{ country.currency_symbol }})</span>
                    </span>
                    <svg v-if="isSelected(country)" class="currency-check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "CurrencySelector",
    props: {
        // "mobile" gets slightly tighter padding/flag size to match the
        // compact top-header slot; "nav" (default) matches the desktop menu.
        variant: {
            type: String,
            default: "nav",
        },
    },
    data() {
        return {
            isOpen: false,
        };
    },
    computed: {
        ...mapGetters(["countries", "selectedCountry"]),
    },
    mounted() {
        document.addEventListener("click", this.handleOutsideClick);
        document.addEventListener("keydown", this.handleKeydown);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleOutsideClick);
        document.removeEventListener("keydown", this.handleKeydown);
    },
    methods: {
        toggle() {
            this.isOpen = !this.isOpen;
        },
        close() {
            this.isOpen = false;
        },
        isSelected(country) {
            return !!(this.selectedCountry && this.selectedCountry.id === country.id);
        },
        select(country) {
            this.$store.dispatch("setSelectedCountry", country);
            this.close();
        },
        handleOutsideClick(event) {
            if (this.isOpen && this.$refs.wrap && !this.$refs.wrap.contains(event.target)) {
                this.close();
            }
        },
        handleKeydown(event) {
            if (event.key === "Escape" && this.isOpen) {
                this.close();
                this.$refs.wrap.querySelector(".currencySelect").focus();
            }
        },
    },
};
</script>

<style scoped>
.currency-toggle-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
}
.currencySelect {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    appearance: none;
    font: inherit;
    color: var(--text-color);
    font-size: 14px;
    font-weight: 600;
    padding: 6px 12px 6px 6px;
    cursor: pointer;
    border: 0;
    background: transparent;
    box-shadow: none;
    margin-left: 5px;
}
.currency-code {
    display: inline-block;
    align-self: center;
    line-height: 1;
    letter-spacing: 0.01em;
}
.currency-chevron {
    align-self: center;
    width: 14px;
    height: 14px;
    color: var(--text-muted);
    flex-shrink: 0;
    transition: transform 0.2s ease;
}
.currencySelect[aria-expanded="true"] .currency-chevron {
    transform: rotate(180deg);
    color: var(--brand-color);
}
.currency-flag-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 0 0 1px var(--border-color);
    flex-shrink: 0;
}
.currency-flag {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.is-mobile .currencySelect {
    padding: 4px 10px 4px 4px;
}
.is-mobile .currency-flag-wrap {
    width: 26px;
    height: 26px;
}

.currencyMenu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1050;
    min-width: 250px;
    max-height: 340px;
    overflow-y: auto;
    padding: 8px;
    margin: 0;
    background: var(--surface-color);
    border-radius: 20px;
}
.currencyMenu .dropdown-header {
    padding: 8px 10px 10px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
}
.currencyMenu .dropdown-item {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
    appearance: none;
    font: inherit;
    text-align: left;
    padding: 10px 12px 10px 14px;
    margin: 1px 0;
    border: 0;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-color);
    background: transparent;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}
.currencyMenu .dropdown-item:hover,
.currencyMenu .dropdown-item:focus {
    background-color: var(--surface-alt-color);
    color: var(--text-color);
    outline: none;
}
.currencyMenu .dropdown-item .currency-item-text {
    display: inline-flex;
    align-items: baseline;
    align-self: center;
    gap: 4px;
    line-height: 1;
}
.currency-item-code {
    line-height: 1;
}
.currency-item-symbol {
    line-height: 1;
    color: var(--text-muted);
    font-size: 12px;
    font-weight: 400;
}
.currencyMenu .dropdown-item.active {
    background-color: var(--tint-surface-color);
    color: var(--text-color);
}
.currencyMenu .dropdown-item.active::before {
    content: "";
    position: absolute;
    left: 2px;
    top: 20%;
    bottom: 20%;
    width: 3px;
    border-radius: 3px;
    background: var(--brand-color);
}
.currencyMenu .dropdown-item.active .currency-item-text {
    font-weight: 700;
}
.currency-check {
    align-self: center;
    width: 16px;
    height: 16px;
    margin-left: auto;
    color: var(--brand-color);
    flex-shrink: 0;
}

.currency-menu-enter-active,
.currency-menu-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.currency-menu-enter,
.currency-menu-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
